// Admin CRUD for vendor_types (migration 063). New types appear in the vendor
// form's picker the same session, no deploy.
//
// WHY A TABLE AND NOT A CONSTANT. The taxonomy was a frozen array in
// vendors-service.js for three releases, so adding a type meant a deploy. It
// groups the vendor list and nothing else hangs off it, which is Odoo's partner
// tag rather than ERPNext's Supplier Group (that one carries payment terms and a
// payable account, and ours carries no behavior at all).
//
// WHY THIS FILE IS ONLY A CONFIG. The page is TaxonomyAdmin, shared with item
// kinds: same row shape, same two rules (a key never changes, retire beats
// delete), same save-on-blur cells. Everything below is the copy.

import React from 'react';
import TaxonomyAdmin, { TaxonomyConfig, TaxonomyRow } from './TaxonomyAdmin';

export interface VendorType extends TaxonomyRow {
  vendor_count: number;
}

const CONFIG: TaxonomyConfig<VendorType> = {
  title: 'Vendor Types',
  endpoint: '/procurement/vendor-types',
  noun: 'vendor type',
  nounPlural: 'vendor types',
  countField: 'vendor_count',
  countHeader: 'Vendors',
  newButtonLabel: '+ New Type',
  submitLabel: 'Add type',
  deleteBlockedTitle: (n) =>
    `${n} vendor${n === 1 ? '' : 's'} use${n === 1 ? 's' : ''} this type. Retire it instead.`,
  keyColumn: {
    header: 'Key',
    // Snake case, because vendors.vendor_type stores this string and the rest of
    // the procurement schema is spelled that way.
    transform: (label) =>
      label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
    placeholder: 'wire_harness',
    hint: 'What the database stores. It never changes after this, so the name above is the one to get wrong safely.',
  },
  help: (
    <>
      <p>
        A vendor type is how the vendor list groups itself. It carries no behavior: no
        payment terms, no accounts, nothing that changes what an order does.
      </p>
      <p>
        <b>Renaming is free.</b> Vendors are attached by an internal key, not by the name
        on screen, so changing a label re-labels every vendor on it at once and breaks
        nothing.
      </p>
      <p>
        <b>The key never changes.</b> It is what the database stores. To change one, make a
        new type and move the vendors over.
      </p>
      <p>
        <b>Retire rather than delete.</b> Retiring hides a type from the picker for new
        vendors while leaving the ones already on it alone. Delete only works while no
        vendor uses the type at all.
      </p>
      <p>
        <b>Order</b> sets the order the groups appear in on the vendor list. Lower first.
      </p>
    </>
  ),
};

const VendorTypesAdmin: React.FC = () => <TaxonomyAdmin config={CONFIG} />;

export default VendorTypesAdmin;
